/**
 * コンタクト（企業ごとの 1 スレッド）とメッセージの型と入力検証（02 5.3.18 contact-thread・ブリーフ C-02・13 章 C-31）。
 * - スレッドは企業ごとに 1 つ（主キー customer_code）。グループ名・参加者・区分の項目は持たず、更新の操作も無い（README 判断事項 No.2）
 * - メッセージの件名・本文の必須の文言は手段ごと（メール MSG-V-056・091、電話 MSG-V-056・080、会議 MSG-V-045・046）
 * - メールの送信は相手先の担当者（企業担当者）が必須（MSG-V-076）。宛先の自由入力・添付は受け付けない
 */
import { z } from 'zod';
import { ACTIVITY_TYPE_VALUES } from '../enums/activity-type';
import { CONTACT_CHANNEL_TYPE_VALUES } from '../enums/contact-channel-type';
import { MESSAGE_DIRECTION_TYPE_VALUES } from '../enums/message-direction-type';
import { SALES_PHASE_TYPE_VALUES } from '../enums/sales-phase-type';
import { ActivitySchema } from './activity.schema';
import { zCode, zDateTime, zEnum, zNullable, zShortText, zSystemCode } from './common.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const ContactThreadSchema = z
  .object({
    customer_code: z.string(),
    customer_name: z.string().nullable(),
    /** 主担当のメール（MSG-E-018 の判定用） */
    primary_customer_contact_email: z.string().nullable(),
    last_message_at: z.string().nullable(),
    message_count: z.number().int(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-THRD-02 コンタクトの開始（企業ごとに 1 つ） */
export const ContactThreadCreateSchema = z.object({ customer_code: zCode }).strict();

export const ContactMessageSchema = z
  .object({
    customer_code: z.string(),
    contact_message_code: z.string(),
    contact_channel_type: z.enum(CONTACT_CHANNEL_TYPE_VALUES),
    message_direction_type: z.enum(MESSAGE_DIRECTION_TYPE_VALUES),
    subject: z.string(),
    message_body: z.string(),
    sent_at: z.string(),
    customer_contact_code: z.string().nullable(),
    customer_contact_name: z.string().nullable(),
    recorded_by: z.string(),
    recorded_by_name: z.string().nullable(),
    reply_to_message_code: z.string().nullable(),
    activity_code: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** 手段ごとの必須の文言と本文の上限 */
const CHANNEL_RULES = {
  email: { subject_message: 'MSG-V-056', body_message: 'MSG-V-091', body_max: 10000 },
  phone: { subject_message: 'MSG-V-056', body_message: 'MSG-V-080', body_max: 5000 },
  meeting: { subject_message: 'MSG-V-045', body_message: 'MSG-V-046', body_max: 5000 },
} as const;

/** API-THRD-06 メッセージの送信（メール）・電話／会議の記録 */
export const ContactMessageCreateSchema = z
  .object({
    contact_channel_type: zEnum(CONTACT_CHANNEL_TYPE_VALUES),
    message_direction_type: zEnum(MESSAGE_DIRECTION_TYPE_VALUES).optional(),
    subject: z.string({ message: 'MSG-V-056' }).trim().max(500, 'MSG-V-005'),
    message_body: z.string({ message: 'MSG-V-091' }).trim().max(10000, 'MSG-V-005'),
    sent_at: zDateTime.optional(),
    customer_contact_code: zNullable(zSystemCode).optional(),
    reply_to_message_code: zNullable(zSystemCode).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const rules = CHANNEL_RULES[value.contact_channel_type];
    if (value.subject === '') {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['subject'], message: rules.subject_message });
    }
    if (value.message_body === '') {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['message_body'], message: rules.body_message });
    } else if (value.message_body.length > rules.body_max) {
      context.addIssue({
        code: z.ZodIssueCode.too_big,
        path: ['message_body'],
        maximum: rules.body_max,
        type: 'string',
        inclusive: true,
        message: 'MSG-V-005',
      });
    }
    const direction = value.message_direction_type ?? 'outbound';
    if (value.contact_channel_type === 'email' && direction === 'outbound' && !value.customer_contact_code) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['customer_contact_code'], message: 'MSG-V-076' });
    }
  });

/** API-THRD-08 メッセージから活動記録を作成（activity_type の既定は email→email、phone→phone、meeting→visit） */
export const ContactActivityCreateSchema = z
  .object({
    activity_type: zEnum(ACTIVITY_TYPE_VALUES).optional(),
    case_code: zNullable(zCode).optional(),
    sales_phase_type: zNullable(zEnum(SALES_PHASE_TYPE_VALUES)).optional(),
    activity_result: zShortText(2000).optional(),
  })
  .strict();

export const ContactActivityResultSchema = z.object({ activity: ActivitySchema, message: ContactMessageSchema });

export const CONTACT_THREAD_LIST_SORT_VALUES = ['last_message_at', 'customer_name', 'created_at'] as const;

/** API-THRD-01 コンタクトの一覧（q は企業名の部分一致・顧客コードの完全一致） */
export const ContactThreadListQuerySchema = buildListQuerySchema(CONTACT_THREAD_LIST_SORT_VALUES, '-last_message_at');

export const CONTACT_MESSAGE_LIST_SORT_VALUES = ['sent_at'] as const;

/** API-THRD-05 メッセージ一覧（q は件名・本文の部分一致） */
export const ContactMessageListQuerySchema = buildListQuerySchema(CONTACT_MESSAGE_LIST_SORT_VALUES, '-sent_at')
  .omit({ deleted: true })
  .extend({ contact_channel_type: zEnum(CONTACT_CHANNEL_TYPE_VALUES).optional() });

export type ContactThread = z.infer<typeof ContactThreadSchema>;
export type ContactThreadCreateInput = z.infer<typeof ContactThreadCreateSchema>;
export type ContactMessage = z.infer<typeof ContactMessageSchema>;
export type ContactMessageCreateInput = z.infer<typeof ContactMessageCreateSchema>;
export type ContactActivityCreateInput = z.infer<typeof ContactActivityCreateSchema>;
export type ContactActivityResult = z.infer<typeof ContactActivityResultSchema>;
export type ContactThreadListQuery = z.infer<typeof ContactThreadListQuerySchema>;
export type ContactMessageListQuery = z.infer<typeof ContactMessageListQuerySchema>;
